import React, { createContext, useEffect, useState } from 'react'

// The resolved theme actually applied to the document
type Theme = 'light' | 'dark'
// The user's preference — 'system' follows the OS setting
type ThemeSetting = 'light' | 'dark' | 'system'

interface ThemeContextType {
    // The resolved theme currently applied ('light' | 'dark')
    theme: Theme
    // The user's selected preference ('light' | 'dark' | 'system')
    themeSetting: ThemeSetting
    setTheme: (theme: ThemeSetting) => void
    toggleTheme: () => void
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeProviderProps {
    children: React.ReactNode
}

const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)'

function getSystemTheme(): Theme {
    return window.matchMedia(SYSTEM_DARK_QUERY).matches ? 'dark' : 'light'
}

function resolveTheme(setting: ThemeSetting): Theme {
    return setting === 'system' ? getSystemTheme() : setting
}

export function ThemeProvider({ children }: ThemeProviderProps) {
    // Initialize the preference from localStorage, defaulting to system
    const [themeSetting, setThemeSettingState] = useState<ThemeSetting>(() => {
        const stored = localStorage.getItem('theme') as ThemeSetting | null
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
            return stored
        }
        return 'system'
    })

    // The resolved theme that gets applied to the document
    const [theme, setTheme] = useState<Theme>(() => resolveTheme(themeSetting))

    // Keep the resolved theme in sync with the preference
    useEffect(() => {
        setTheme(resolveTheme(themeSetting))
        localStorage.setItem('theme', themeSetting)
    }, [themeSetting])

    // When following the system, react to OS-level theme changes
    useEffect(() => {
        if (themeSetting !== 'system') return

        const mediaQuery = window.matchMedia(SYSTEM_DARK_QUERY)
        const handleChange = (event: MediaQueryListEvent) => {
            setTheme(event.matches ? 'dark' : 'light')
        }

        mediaQuery.addEventListener('change', handleChange)
        return () => mediaQuery.removeEventListener('change', handleChange)
    }, [themeSetting])

    // Update document class and theme-color meta tags when the resolved theme changes
    useEffect(() => {
        const root = window.document.documentElement
        root.classList.remove('light', 'dark')
        root.classList.add(theme)

        // Sync theme-color meta tags so browser/OS chrome matches the in-app theme
        const themeColor = theme === 'dark' ? '#000000' : '#FFFFFF'
        document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach(meta => {
            meta.setAttribute('content', themeColor)
        })
    }, [theme])

    const setThemeSetting = (newTheme: ThemeSetting) => {
        setThemeSettingState(newTheme)
    }

    const toggleTheme = () => {
        // Toggle based on the currently resolved theme, switching to an explicit preference
        setThemeSettingState(theme === 'light' ? 'dark' : 'light')
    }

    return (
        <ThemeContext.Provider
            value={{ theme, themeSetting, setTheme: setThemeSetting, toggleTheme }}
        >
            {children}
        </ThemeContext.Provider>
    )
}
